/**
 * Rule evaluator for component rules.
 *
 * Pure functions — **no React imports, no Zod imports, no fetch**. This
 * file owns the runtime semantics of the {@link ComponentRule} type:
 *
 * 1. **Condition evaluation** — walk a {@link FieldCondition} tree against a
 *    {@link RuleContextValue} and return a boolean.
 * 2. **Effect application** — fold an ordered {@link RuleEffect}[] into a
 *    new `{ props, hidden, requiredProps, disabled }` bundle.
 * 3. **Rule application** — compose both steps across a list of rules,
 *    honouring `status === 'active'` and `priority` ordering.
 *
 * A fresh evaluator — **not** reusing form-rules' `conditionEvaluator` —
 * because the component-rule leaf shape differs: it uses `field` (not
 * `when`) and has no per-leaf `effect`. The 11-operator vocabulary is the
 * same, but the AND/OR tree walk is implemented here from scratch.
 */

import type { RuleFieldCondition } from '@components/integrated/entity-form';

import { DEFAULT_REQUIRED_MESSAGE } from './layoutOverrides';
import type {
  ActionOverride,
  ColumnOverride,
  DefaultSortOverride,
  FieldOverride,
  FilterOverride,
  LayoutOverrides,
  SearchOverride,
  SectionOverride,
} from './layoutOverrides';
import type {
  ComponentRule,
  FieldCondition,
  RuleContextValue,
  RuleEffect,
  SimpleCondition,
} from './types';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Type guard distinguishing a {@link SimpleCondition} leaf from a
 * {@link ConditionGroup} wrapper. Leaves carry a `field` string; groups
 * carry a `conditions` array.
 */
function isSimpleCondition(node: FieldCondition): node is SimpleCondition {
  return (
    typeof (node as SimpleCondition).field === 'string' &&
    (node as SimpleCondition).field.length > 0
  );
}

/**
 * True when a value matches the "empty" definition used by `isEmpty` /
 * `isNotEmpty` operators: `undefined`, `null`, empty string, empty array.
 */
function isValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string' && value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Deep-compare two arrays by JSON stringification. Used by `equals` /
 * `notEquals` when both operands are arrays — cheaper than a custom
 * recursive walker and sufficient for the primitive values component rules
 * deal with.
 */
function deepArrayEquals(a: unknown[], b: unknown[]): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * Evaluate a single {@link SimpleCondition} leaf against the rule context.
 *
 * Returns `false` for unknown context variables on every operator except
 * `isEmpty` (which is `true` by definition — a missing variable is empty).
 * `matches` compiles the value as a regex source and refuses patterns
 * longer than 200 characters as a defence against ReDoS.
 */
function evaluateLeaf(
  node: SimpleCondition,
  context: RuleContextValue,
): boolean {
  const { field, operator, value } = node;
  const contextValue = context[field];
  const isMissing = contextValue === undefined;

  if (isMissing) {
    if (operator === 'isEmpty') return true;
    if (operator === 'isNotEmpty') return false;
    return false;
  }

  switch (operator) {
    case 'equals': {
      if (Array.isArray(contextValue) && Array.isArray(value)) {
        return deepArrayEquals(contextValue, value);
      }
      return contextValue === value;
    }
    case 'notEquals': {
      if (Array.isArray(contextValue) && Array.isArray(value)) {
        return !deepArrayEquals(contextValue, value);
      }
      return contextValue !== value;
    }
    case 'contains': {
      if (typeof contextValue === 'string' && typeof value === 'string') {
        return contextValue.includes(value);
      }
      if (Array.isArray(contextValue)) {
        return contextValue.includes(value);
      }
      return false;
    }
    case 'notContains': {
      if (typeof contextValue === 'string' && typeof value === 'string') {
        return !contextValue.includes(value);
      }
      if (Array.isArray(contextValue)) {
        return !contextValue.includes(value);
      }
      return false;
    }
    case 'greaterThan': {
      const lhs = Number(contextValue);
      const rhs = Number(value);
      if (Number.isNaN(lhs) || Number.isNaN(rhs)) return false;
      return lhs > rhs;
    }
    case 'lessThan': {
      const lhs = Number(contextValue);
      const rhs = Number(value);
      if (Number.isNaN(lhs) || Number.isNaN(rhs)) return false;
      return lhs < rhs;
    }
    case 'isEmpty': {
      return isValueEmpty(contextValue);
    }
    case 'isNotEmpty': {
      return !isValueEmpty(contextValue);
    }
    case 'in': {
      if (!Array.isArray(value)) return false;
      return value.includes(contextValue);
    }
    case 'notIn': {
      if (!Array.isArray(value)) return false;
      return !value.includes(contextValue);
    }
    case 'matches': {
      if (typeof value !== 'string') return false;
      if (value.length > 200) return false;
      try {
        const regex = new RegExp(value);
        // Coerce the context value to a string deterministically.
        // Primitives use their native `toString`; objects / arrays
        // fall back to `JSON.stringify` so the regex never sees the
        // bare `[object Object]` default of `Object.prototype.toString`.
        // `contextValue` is already known to be defined here — the
        // `isMissing` guard at the top of this function returned false
        // on every operator except `isEmpty` / `isNotEmpty`.
        let haystack: string;
        if (typeof contextValue === 'string') {
          haystack = contextValue;
        } else if (
          typeof contextValue === 'number' ||
          typeof contextValue === 'boolean' ||
          typeof contextValue === 'bigint'
        ) {
          haystack = contextValue.toString();
        } else {
          haystack = JSON.stringify(contextValue);
        }
        return regex.test(haystack);
      } catch {
        return false;
      }
    }
    default: {
      return false;
    }
  }
}

// ---------------------------------------------------------------------------
// Public API — condition tree
// ---------------------------------------------------------------------------

/**
 * Evaluate a component-rule condition tree against a rule context.
 *
 * - `undefined` node → vacuously `true` (a rule with no conditions always
 *   fires).
 * - Leaf nodes are dispatched to {@link evaluateLeaf} using the 11-operator
 *   vocabulary shared with form rules.
 * - Group nodes recurse with AND / OR short-circuit semantics; empty
 *   groups follow the mathematical conventions (AND of zero things is
 *   `true`, OR of zero things is `false`) so the evaluator matches the
 *   form-rules behaviour.
 */
export function evaluateConditionTree(
  node: FieldCondition | undefined,
  context: RuleContextValue,
): boolean {
  if (node === undefined) return true;

  if (isSimpleCondition(node)) {
    return evaluateLeaf(node, context);
  }

  // After the guard, TypeScript narrows `node` to `ConditionGroup`.
  const { operator, conditions } = node;

  if (operator === 'AND') {
    if (conditions.length === 0) return true;
    for (const child of conditions) {
      if (!evaluateConditionTree(child, context)) {
        return false;
      }
    }
    return true;
  }

  // OR
  if (conditions.length === 0) return false;
  for (const child of conditions) {
    if (evaluateConditionTree(child, context)) {
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Effect application
// ---------------------------------------------------------------------------

/**
 * Shape returned by {@link applyRuleEffects} and {@link applyRules}. Keeps
 * the "meta" output separate from the mutated props so the HOC can make
 * render-time decisions (hide / disable / require) without mingling them
 * with the wrapped component's own props surface.
 */
export interface RuleEffectResult<P> {
  /** Shallow-cloned props with `setProp` / `filterPropValues` applied. */
  props: P;
  /** True when a `hide` effect fired without a later `show` overriding it. */
  hidden: boolean;
  /** Prop names accumulated by `requireProp` effects, in order. */
  requiredProps: string[];
  /** True when a `disable` effect fired. */
  disabled: boolean;
  /**
   * Accumulated layout / tooltip mutations from the `renameField` /
   * `hideField` / `reorderFields` (and column / section / tooltip) effects,
   * keyed by stable field / column / section key. `undefined` when no such
   * effect fired, so a migrated component sees identity (zero visible change)
   * exactly as it does with no rules authored. The HOC forwards this as the
   * `_layoutOverrides` prop. See {@link LayoutOverrides}.
   */
  layoutOverrides?: LayoutOverrides;
}

// ---------------------------------------------------------------------------
// Layout-override accumulation
// ---------------------------------------------------------------------------

/**
 * Mutable mirror of {@link LayoutOverrides} used while folding effects. Each
 * bucket is created lazily on first write so an empty accumulator stays
 * `undefined` everywhere (and ultimately collapses to no `layoutOverrides` at
 * all — see {@link finaliseLayoutOverrides}).
 */
interface LayoutAccumulator {
  fields?: Record<string, FieldOverride>;
  sections?: Record<string, SectionOverride>;
  columns?: Record<string, ColumnOverride>;
  /**
   * Single default-sort value (not a keyed bucket — a surface has exactly one
   * initial sort). Last `setDefaultSort` write across all firing rules wins.
   */
  defaultSort?: DefaultSortOverride;
  /**
   * Single manual row-order value (data-row-ordering Tier 2). Not a keyed
   * bucket — a surface has exactly one manual order — so the last `reorderRows`
   * write across all firing rules wins, mirroring {@link defaultSort}.
   */
  rowOrder?: string[];
  /**
   * Single component-title value. Not a keyed bucket — a surface has one title
   * — so the last `setTitle` write across all firing rules wins, mirroring
   * {@link defaultSort}.
   */
  title?: string;
  /** Action-button overrides keyed by action key (`submit` / `cancel` / `reset` / custom id). */
  actions?: Record<string, ActionOverride>;
  /** DataList toolbar-filter overrides keyed by stable filter id. */
  filters?: Record<string, FilterOverride>;
  /**
   * Single toolbar **search** override (placeholder / hidden). Not a keyed
   * bucket — one search box per toolbar — so later writes merge onto the same
   * slot (last-write-wins per property), mirroring {@link defaultSort}.
   */
  search?: SearchOverride;
}

/** Lazily get-or-create the `fields[key]` override slot on the accumulator. */
function fieldSlot(acc: LayoutAccumulator, key: string): FieldOverride {
  const bucket = (acc.fields ??= {});
  return (bucket[key] ??= {});
}

/** Lazily get-or-create the `columns[key]` override slot on the accumulator. */
function columnSlot(acc: LayoutAccumulator, key: string): ColumnOverride {
  const bucket = (acc.columns ??= {});
  return (bucket[key] ??= {});
}

/** Lazily get-or-create the `sections[key]` override slot on the accumulator. */
function sectionSlot(acc: LayoutAccumulator, key: string): SectionOverride {
  const bucket = (acc.sections ??= {});
  return (bucket[key] ??= {});
}

/** Lazily get-or-create the `actions[key]` override slot on the accumulator. */
function actionSlot(acc: LayoutAccumulator, key: string): ActionOverride {
  const bucket = (acc.actions ??= {});
  return (bucket[key] ??= {});
}

/** Lazily get-or-create the `filters[key]` override slot on the accumulator. */
function filterSlot(acc: LayoutAccumulator, key: string): FilterOverride {
  const bucket = (acc.filters ??= {});
  return (bucket[key] ??= {});
}

/** Lazily get-or-create the single `search` override slot on the accumulator. */
function searchSlot(acc: LayoutAccumulator): SearchOverride {
  return (acc.search ??= {});
}

/**
 * Apply a single layout / tooltip effect onto the mutable accumulator. Walks
 * the effects in fold order, so a later effect overwrites an earlier one for
 * the same key + property (last-write-wins; the same precedence the prop
 * effects already use). Reorder effects assign an incrementing `order`
 * (0, 1, 2, …) by each key's position in the `order[]` array, so a later
 * reorder of the same bucket fully replaces the earlier positions.
 *
 * Returns `true` if the effect was a layout effect it consumed (so the caller
 * can skip its own prop handling), `false` otherwise.
 */
function applyLayoutEffect(acc: LayoutAccumulator, effect: RuleEffect): boolean {
  switch (effect.type) {
    case 'renameField': {
      fieldSlot(acc, effect.key).label = effect.label;
      return true;
    }
    case 'hideField': {
      fieldSlot(acc, effect.key).visible = false;
      return true;
    }
    case 'setFieldTooltip': {
      fieldSlot(acc, effect.key).tooltip = effect.tooltip;
      return true;
    }
    case 'reorderFields': {
      effect.order.forEach((key, index) => {
        fieldSlot(acc, key).order = index;
      });
      return true;
    }
    case 'renameColumn': {
      columnSlot(acc, effect.key).label = effect.label;
      return true;
    }
    case 'hideColumn': {
      columnSlot(acc, effect.key).hidden = true;
      return true;
    }
    case 'setColumnTooltip': {
      const slot = columnSlot(acc, effect.key);
      if (effect.target === 'cell') {
        slot.cellTooltip = { text: effect.tooltip };
      } else {
        slot.headerTooltip = effect.tooltip;
      }
      return true;
    }
    case 'reorderColumns': {
      effect.order.forEach((key, index) => {
        columnSlot(acc, key).order = index;
      });
      return true;
    }
    case 'reorderSections': {
      effect.order.forEach((key, index) => {
        sectionSlot(acc, key).order = index;
      });
      return true;
    }
    case 'setDefaultSort': {
      // A surface has one initial sort — last write wins (priority then array
      // order), like the per-key layout effects.
      acc.defaultSort = { column: effect.column, direction: effect.direction };
      return true;
    }
    case 'reorderRows': {
      // A surface has one manual row order — last write wins (priority then
      // array order), like `setDefaultSort`. Copy the array so a later mutation
      // of the source effect can't bleed into the accumulator.
      acc.rowOrder = [...effect.order];
      return true;
    }
    case 'setFieldPlaceholder': {
      fieldSlot(acc, effect.key).placeholder = effect.placeholder;
      return true;
    }
    case 'setFieldHelperText': {
      fieldSlot(acc, effect.key).helperText = effect.helperText;
      return true;
    }
    case 'requireField': {
      // Add-only: mark the field client-side required. Fall back to the single
      // shared default message when the effect omits/blanks `message`, so the
      // default text lives in exactly one place (`DEFAULT_REQUIRED_MESSAGE`).
      const slot = fieldSlot(acc, effect.key);
      slot.required = true;
      slot.requiredMessage =
        effect.message !== undefined && effect.message.trim() !== ''
          ? effect.message
          : DEFAULT_REQUIRED_MESSAGE;
      return true;
    }
    case 'clearFieldTooltip': {
      // Set the explicit "force off" sentinel and drop any earlier tooltip on
      // the same slot so the two can't coexist (clear is deterministic last
      // word for the key's tooltip).
      const slot = fieldSlot(acc, effect.key);
      slot.clearTooltip = true;
      delete slot.tooltip;
      return true;
    }
    case 'clearColumnTooltip': {
      const slot = columnSlot(acc, effect.key);
      if (effect.target === 'cell') {
        slot.clearCellTooltip = true;
        delete slot.cellTooltip;
      } else {
        slot.clearHeaderTooltip = true;
        delete slot.headerTooltip;
      }
      return true;
    }
    case 'setTitle': {
      // A surface has one title — last write wins (priority then array order),
      // like `setDefaultSort`.
      acc.title = effect.title;
      return true;
    }
    case 'setActionLabel': {
      actionSlot(acc, effect.action).label = effect.label;
      return true;
    }
    case 'hideAction': {
      actionSlot(acc, effect.action).visible = false;
      return true;
    }
    case 'showField': {
      fieldSlot(acc, effect.key).visible = true;
      return true;
    }
    case 'showColumn': {
      columnSlot(acc, effect.key).hidden = false;
      return true;
    }
    case 'renameFilter': {
      filterSlot(acc, effect.key).label = effect.label;
      return true;
    }
    case 'hideFilter': {
      filterSlot(acc, effect.key).hidden = true;
      return true;
    }
    case 'showFilter': {
      filterSlot(acc, effect.key).hidden = false;
      return true;
    }
    case 'reorderFilters': {
      effect.order.forEach((key, index) => {
        filterSlot(acc, key).order = index;
      });
      return true;
    }
    case 'setFilterPlaceholder': {
      filterSlot(acc, effect.key).placeholder = effect.placeholder;
      return true;
    }
    case 'setSearchPlaceholder': {
      searchSlot(acc).placeholder = effect.placeholder;
      return true;
    }
    case 'hideSearch': {
      searchSlot(acc).hidden = true;
      return true;
    }
    case 'showSearch': {
      searchSlot(acc).hidden = false;
      return true;
    }
    case 'setFieldCondition': {
      // The one APPEND-only field slot: several effects may attach several
      // runtime conditions to the same field, and they apply in fold order
      // (last matching wins at evaluation time). Copy the payload so a later
      // mutation of the source effect can't bleed into the accumulator, and
      // keep `value` ABSENT when the effect omits it (isEmpty / isNotEmpty).
      const slot = fieldSlot(acc, effect.key);
      const condition: RuleFieldCondition = {
        when: effect.when,
        operator: effect.operator,
        effect: { ...effect.effect },
      };
      if (effect.value !== undefined) condition.value = effect.value;
      (slot.conditions ??= []).push(condition);
      return true;
    }
    default: {
      // Not a layout effect — the caller handles it.
      return false;
    }
  }
}

/**
 * Collapse a mutable {@link LayoutAccumulator} to a serialisable
 * {@link LayoutOverrides}, or `undefined` when no layout effect ever fired.
 * Keeping the empty case `undefined` preserves the identity guarantee: a
 * component with no matching layout rules receives no `_layoutOverrides` prop
 * and `applyLayoutOverrides` returns its config unchanged.
 */
function finaliseLayoutOverrides(
  acc: LayoutAccumulator,
): LayoutOverrides | undefined {
  if (
    acc.fields === undefined &&
    acc.sections === undefined &&
    acc.columns === undefined &&
    acc.filters === undefined &&
    acc.search === undefined &&
    acc.defaultSort === undefined &&
    acc.rowOrder === undefined &&
    acc.title === undefined &&
    acc.actions === undefined
  ) {
    return undefined;
  }
  const overrides: LayoutOverrides = {};
  if (acc.fields !== undefined) overrides.fields = acc.fields;
  if (acc.sections !== undefined) overrides.sections = acc.sections;
  if (acc.columns !== undefined) overrides.columns = acc.columns;
  if (acc.filters !== undefined) overrides.filters = acc.filters;
  if (acc.search !== undefined) overrides.search = acc.search;
  if (acc.defaultSort !== undefined) overrides.defaultSort = acc.defaultSort;
  if (acc.rowOrder !== undefined) overrides.rowOrder = acc.rowOrder;
  if (acc.title !== undefined) overrides.title = acc.title;
  if (acc.actions !== undefined) overrides.actions = acc.actions;
  return overrides;
}

/**
 * `filterPropValues` helper — handles the two shapes we care about:
 *
 * 1. Arrays of primitives (strings, numbers) — filter by stringified
 *    membership in `allowed`.
 * 2. Arrays of objects with a `CD_GAR` and/or `LIB_GAR` string field
 *    (matches the `VeosGuaranteeToOpen` ERP convention used by
 *    `OpenCoverageCard`) — an item is kept when `allowed` contains
 *    **either** its `CD_GAR` (3-digit ERP code) **or** its `LIB_GAR`
 *    (user-visible label). The label fallback exists because admins
 *    naturally author rules against the labels the `OpenCoverageCard`
 *    checkbox group actually displays, while the CD_GAR path is
 *    preserved for backward compatibility with rules authored against
 *    the raw code.
 *
 * Anything else is skipped with a warning so an admin misconfiguration
 * doesn't silently drop live data.
 */
function filterPropValues(
  props: Record<string, unknown>,
  prop: string,
  allowed: ReadonlyArray<string | number>,
): void {
  const raw = props[prop];
  if (!Array.isArray(raw)) {
    console.warn(
      `[rules] filterPropValues: prop "${prop}" is not a filterable array shape`,
    );
    return;
  }

  const allowedSet = new Set(allowed.map((v) => String(v)));

  const filtered = raw.filter((item: unknown) => {
    if (item !== null && typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      const cdGar = obj.CD_GAR;
      const libGar = obj.LIB_GAR;
      if (typeof cdGar === 'string' && allowedSet.has(cdGar)) return true;
      if (typeof libGar === 'string' && allowedSet.has(libGar)) return true;
      return false;
    }
    return allowedSet.has(String(item));
  });

  // Write back on a shallow clone semantics — the caller already gave us
  // a shallow clone, so a direct assignment is safe.
  props[prop] = filtered;
}

/**
 * Fold an ordered list of {@link RuleEffect}s into a new result bundle.
 *
 * Effects are applied **in order** so two effects on the same prop
 * compose deterministically: a later `setProp` overrides an earlier one,
 * and a `show` dominates any earlier `hide` within the same rule. Layout /
 * tooltip effects compose the same way — a later effect overwrites an
 * earlier one for the same key + property (last-write-wins). The returned
 * `props` object is a shallow clone of `baseProps` — mutations never leak up
 * to the caller.
 *
 * @param sharedLayout - Optional accumulator threaded by {@link applyRules}
 *   so layout effects from *several* rules fold into one object in priority
 *   then array order. Omitted callers get a fresh accumulator (single-rule
 *   semantics). Internal parameter; not part of the public contract.
 */
export function applyRuleEffects<P extends Record<string, unknown>>(
  baseProps: P,
  effects: readonly RuleEffect[],
  sharedLayout?: LayoutAccumulator,
): RuleEffectResult<P> {
  // Shallow clone so effect mutations do not leak back to the caller.
  // The spread preserves the concrete `P` shape at the type level.
  const props: P = { ...baseProps };
  const propsRecord = props as Record<string, unknown>;
  let hidden = false;
  let disabled = false;
  const requiredProps: string[] = [];
  const layout: LayoutAccumulator = sharedLayout ?? {};

  for (const effect of effects) {
    switch (effect.type) {
      case 'hide': {
        hidden = true;
        break;
      }
      case 'show': {
        hidden = false;
        break;
      }
      case 'disable': {
        disabled = true;
        break;
      }
      case 'setProp': {
        propsRecord[effect.prop] = effect.value;
        break;
      }
      case 'filterPropValues': {
        filterPropValues(propsRecord, effect.prop, effect.allowed);
        break;
      }
      case 'requireProp': {
        requiredProps.push(effect.prop);
        break;
      }
      default: {
        // Layout / tooltip effects accumulate into the `layout` bag instead
        // of mutating props. `applyLayoutEffect` returns `false` only for an
        // effect type it does not recognise — which, given the union, cannot
        // happen, but keeps this branch total without a `never` assertion
        // (the union now also covers the layout arms).
        applyLayoutEffect(layout, effect);
        break;
      }
    }
  }

  const layoutOverrides = finaliseLayoutOverrides(layout);
  return {
    props,
    hidden,
    requiredProps,
    disabled,
    ...(layoutOverrides === undefined ? {} : { layoutOverrides }),
  };
}

// ---------------------------------------------------------------------------
// Full rule application
// ---------------------------------------------------------------------------

/**
 * Apply every matching active rule to a component's props.
 *
 * 1. Keeps only rules with `status === 'active'` so drafts and archived
 *    rules never fire at runtime.
 * 2. Sorts the remaining rules by `priority` ASC, breaking ties by `id`
 *    for a stable deterministic order.
 * 3. Folds each firing rule's effects into the running accumulator, so
 *    later rules see the earlier rules' mutations.
 *
 * Layout / tooltip effects fold into a **single** accumulator shared across
 * every firing rule, so precedence is: by rule `priority` ASC (ties by `id`),
 * then by each rule's effect array order — the last write to a given
 * key + property wins. With no layout effect firing the result carries no
 * `layoutOverrides` at all (identity for migrated components).
 *
 * The caller (the HOC) supplies the initial `baseProps` and rule
 * `context`, memoises on their identities, and passes the result directly
 * into the wrapped component.
 */
export function applyRules<P extends Record<string, unknown>>(
  baseProps: P,
  rules: readonly ComponentRule[],
  context: RuleContextValue,
): RuleEffectResult<P> {
  const active = rules.filter((rule) => rule.status === 'active');
  const sorted = [...active].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.id.localeCompare(b.id);
  });

  let props: P = { ...baseProps };
  let hidden = false;
  let disabled = false;
  const requiredProps: string[] = [];
  // One accumulator across all firing rules → cross-rule last-write-wins in
  // priority then array order.
  const layout: LayoutAccumulator = {};

  for (const rule of sorted) {
    if (!evaluateConditionTree(rule.conditions, context)) continue;

    // Each rule is a fresh fold over its own effects. `show` inside a
    // rule dominates a `hide` inside that same rule (enforced by
    // `applyRuleEffects`). Across rules we OR the visible flags so a
    // later rule can add a `hide` but cannot retroactively cancel a
    // prior rule's actions on the props. The shared `layout` accumulator
    // threads layout effects across rules in the same priority order.
    const step = applyRuleEffects(props, rule.effects, layout);
    props = step.props;
    if (step.hidden) hidden = true;
    if (step.disabled) disabled = true;
    requiredProps.push(...step.requiredProps);

    // Explicit `show` inside this rule (dominating its own hide) also
    // dominates earlier rules' `hide` in priority order: the admin who
    // authored this rule knowingly said "show" and will expect their
    // rule to be the last word. Detect this by checking whether the
    // rule contained a `show` effect at all.
    if (rule.effects.some((e) => e.type === 'show')) {
      hidden = false;
    }
  }

  const layoutOverrides = finaliseLayoutOverrides(layout);
  return {
    props,
    hidden,
    requiredProps,
    disabled,
    ...(layoutOverrides === undefined ? {} : { layoutOverrides }),
  };
}
